import React, { useState, useEffect } from 'react';
import {
  CreditCard,
  AlertCircle,
  Loader2,
  ExternalLink,
  Users,
  Briefcase,
  Crown,
  ArrowRight,
  Receipt,
  Clock,
} from 'lucide-react';
import subscriptionService from '../../services/subscriptionService';

const SubscriptionSettings = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [subRes, plansRes, invoicesRes] = await Promise.all([
        subscriptionService.getCurrentSubscription(),
        subscriptionService.getPlans(),
        subscriptionService.getInvoices().catch(() => ({ data: [] })),
      ]);
      setSubscription(subRes.data);
      setPlans(plansRes.data);
      setInvoices(invoicesRes.data);
    } catch (err) {
      console.error('Error loading subscription data:', err);
      setError('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId, interval = 'monthly') => {
    setCheckoutLoading(`${planId}-${interval}`);
    setError('');
    try {
      const response = await subscriptionService.createCheckoutSession({
        plan_id: planId,
        billing_interval: interval,
      });
      window.location.href = response.data.checkout_url;
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError(err.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading(null);
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    setError('');
    try {
      const response = await subscriptionService.createPortalSession();
      window.location.href = response.data.portal_url;
    } catch (err) {
      console.error('Error creating portal session:', err);
      setError(err.response?.data?.detail || 'Failed to open billing portal');
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-yellow-500" />
        <span className="ml-2 text-gray-600">Loading subscription...</span>
      </div>
    );
  }

  const isFreeTier = !subscription || !subscription.current_period_end;
  const isPaidTier = subscription && subscription.current_period_end;
  const isTrialing = subscription?.status === 'trialing';
  const isPastDue = subscription?.status === 'past_due';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-2">
          <Crown className="w-6 h-6 text-yellow-500" />
          <h2 className="text-xl font-semibold text-gray-900">Subscription</h2>
        </div>
        <p className="text-gray-600">
          Manage your JustPractice subscription, billing, and plan features.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Past Due Warning */}
      {isPastDue && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-red-800">Payment Past Due</h4>
              <p className="text-sm text-red-700 mt-1">
                Your last payment failed. Please update your payment method to avoid service interruption.
              </p>
              <button
                onClick={handleManageBilling}
                disabled={portalLoading}
                className="mt-2 px-4 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                Update Payment Method
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Current Plan */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-gray-500" />
          Current Plan
        </h3>

        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <span className="text-2xl font-bold text-gray-900">
                {subscription?.plan_name || 'Free'}
              </span>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                subscriptionService.getTierColor(subscription?.tier || 'free')
              }`}>
                {(subscription?.tier || 'free').charAt(0).toUpperCase() + (subscription?.tier || 'free').slice(1)}
              </span>
              {subscription?.status && subscription.status !== 'active' && (
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  subscriptionService.getStatusColor(subscription.status)
                }`}>
                  {subscriptionService.getStatusLabel(subscription.status)}
                </span>
              )}
            </div>

            {isTrialing && subscription.trial_end && (
              <p className="text-sm text-blue-600 mt-1 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                Trial ends {subscriptionService.formatDate(subscription.trial_end)}
              </p>
            )}

            {isPaidTier && !isTrialing && subscription.current_period_end && (
              <p className="text-sm text-gray-500 mt-1">
                {subscription.cancel_at_period_end
                  ? `Cancels on ${subscriptionService.formatDate(subscription.current_period_end)}`
                  : `Renews ${subscriptionService.formatDate(subscription.current_period_end)}`
                }
                {subscription.billing_interval === 'annual' ? ' (annual)' : ' (monthly)'}
              </p>
            )}
          </div>

          {isPaidTier && (
            <button
              onClick={handleManageBilling}
              disabled={portalLoading}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 flex items-center gap-2 disabled:opacity-50"
            >
              {portalLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ExternalLink className="w-4 h-4" />
              )}
              Manage Billing
            </button>
          )}
        </div>

        {/* Usage */}
        {subscription && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 pt-6 border-t border-gray-100">
            <UsageMeter
              icon={Briefcase}
              label="Active Cases"
              current={subscription.case_count}
              max={subscription.max_cases}
            />
            <UsageMeter
              icon={Users}
              label="Team Members"
              current={subscription.user_count}
              max={subscription.max_users}
            />
          </div>
        )}
      </div>

      {/* Plans */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-6">
          {isFreeTier ? 'Choose a Plan' : 'Available Plans'}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {plans.map((plan) => {
            const isCurrent = subscription?.tier === plan.tier;

            return (
              <div
                key={plan.id}
                className={`relative rounded-xl border-2 p-6 transition-all ${
                  isCurrent
                    ? 'border-yellow-400 bg-yellow-50/30'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {isCurrent && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-yellow-500 text-white text-xs font-medium px-3 py-0.5 rounded-full">
                      Current Plan
                    </span>
                  </div>
                )}

                <div className="text-center mb-4">
                  <h4 className="text-lg font-semibold text-gray-900">{plan.name}</h4>
                  <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
                </div>

                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-3xl font-bold text-gray-900">
                      {plan.max_cases ? plan.max_cases.toLocaleString() : 'Unlimited'}
                    </span>
                    <span className="text-gray-500">active cases</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Full-featured practice management</p>
                </div>

                {/* Action buttons */}
                <div className="space-y-2">
                  {isCurrent ? (
                    <button
                      disabled
                      className="w-full py-2 px-4 text-sm font-medium text-gray-500 bg-gray-100 rounded-lg cursor-default"
                    >
                      Current Plan
                    </button>
                  ) : isPaidTier ? (
                    <button
                      onClick={handleManageBilling}
                      disabled={portalLoading}
                      className="w-full py-2 px-4 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      Change Plan
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => handleSubscribe(plan.id, 'monthly')}
                        disabled={checkoutLoading === `${plan.id}-monthly`}
                        className="w-full py-2 px-4 text-sm font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600 disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {checkoutLoading === `${plan.id}-monthly` ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <ArrowRight className="w-4 h-4" />
                        )}
                        Subscribe Monthly
                      </button>
                      <button
                        onClick={() => handleSubscribe(plan.id, 'annual')}
                        disabled={checkoutLoading === `${plan.id}-annual`}
                        className="w-full py-2 px-4 text-sm font-medium text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {checkoutLoading === `${plan.id}-annual` ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <ArrowRight className="w-4 h-4" />
                        )}
                        Subscribe Annual
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Invoices */}
      {invoices.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Receipt className="w-5 h-5 text-gray-500" />
            Billing History
          </h3>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-4 font-medium text-gray-500">Date</th>
                  <th className="text-left py-2 pr-4 font-medium text-gray-500">Amount</th>
                  <th className="text-left py-2 pr-4 font-medium text-gray-500">Status</th>
                  <th className="text-right py-2 font-medium text-gray-500">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b border-gray-100 last:border-0">
                    <td className="py-3 pr-4 text-gray-700">
                      {subscriptionService.formatDate(invoice.created_at)}
                    </td>
                    <td className="py-3 pr-4 text-gray-900 font-medium">
                      {subscriptionService.formatCurrency(invoice.amount_due)}
                    </td>
                    <td className="py-3 pr-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        subscriptionService.getInvoiceStatusColor(invoice.status)
                      }`}>
                        {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      </span>
                    </td>
                    <td className="py-3 text-right">
                      {invoice.invoice_url && (
                        <a
                          href={invoice.invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-yellow-600 hover:text-yellow-700 flex items-center gap-1 justify-end"
                        >
                          View <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {isPaidTier && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <button
                onClick={handleManageBilling}
                disabled={portalLoading}
                className="text-sm text-yellow-600 hover:text-yellow-700 flex items-center gap-1"
              >
                View all invoices in billing portal
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const UsageMeter = ({ icon: Icon, label, current, max, suffix = '' }) => {
  const percentage = max && current != null ? Math.min((current / max) * 100, 100) : null;
  const isNearLimit = percentage !== null && percentage >= 80;

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
        <Icon className="w-4 h-4" />
        <span>{label}</span>
      </div>
      <div className="flex items-baseline gap-1">
        {current != null ? (
          <>
            <span className={`text-xl font-semibold ${isNearLimit ? 'text-red-600' : 'text-gray-900'}`}>
              {current}
            </span>
            {max ? (
              <span className="text-sm text-gray-500">/ {max}{suffix ? ` ${suffix}` : ''}</span>
            ) : (
              <span className="text-sm text-gray-500">{suffix ? ` ${suffix}` : ''} (unlimited)</span>
            )}
          </>
        ) : (
          <span className="text-sm text-gray-500">
            {max ? `${max} ${suffix}` : `Unlimited${suffix ? ` ${suffix}` : ''}`}
          </span>
        )}
      </div>
      {percentage !== null && (
        <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
          <div
            className={`h-1.5 rounded-full transition-all ${
              isNearLimit ? 'bg-red-500' : 'bg-yellow-500'
            }`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default SubscriptionSettings;
